// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useLogin, useMe } from '../../api/auth.js';
import { useAcceptInvite } from '../../api/customer-enablement.js';
import { BrandSymbol, BrandWordmark } from '../../components/BrandMark.js';

export function AcceptInviteRoute() {
  const { token } = useParams<{ token: string }>();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const accept = useAcceptInvite(token);
  const login = useLogin();

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');

  if (me) return <Navigate to="/dashboard" replace />;
  if (!token) {
    return (
      <main className="login-main" id="main-content">
        <div className="login-card">
          <h1 className="login-title">Invalid invite</h1>
          <p>This invite link is missing its token. Ask your administrator to resend it.</p>
        </div>
      </main>
    );
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    accept.mutate(
      { password, displayName: displayName.trim() },
      {
        onSuccess: ({ user }) => {
          login.mutate(
            { email: user.email, password },
            {
              onSuccess: () => navigate('/dashboard', { replace: true }),
              onError: () => navigate('/login', { replace: true }),
            },
          );
        },
      },
    );
  };

  return (
    <main className="login-main" id="main-content">
      <div className="login-card">
        <div className="login-brand">
          <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
          <BrandWordmark className="brand-wordmark" />
        </div>
        <h1 className="login-title">Accept your invite</h1>
        <p className="muted">Choose a password and confirm your name to join the clinic.</p>

        <form onSubmit={onSubmit} aria-label="Accept invite">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label htmlFor="accept-name">
              <span>Your name</span>
              <input
                id="accept-name"
                name="display-name"
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                required
              />
            </label>
            <label htmlFor="accept-password">
              <span>Password (8+ characters)</span>
              <input
                id="accept-password"
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
              />
            </label>
            {accept.error ? (
              <div className="error-banner" role="alert" aria-live="polite">
                {accept.error.code === 'consumed'
                  ? 'This invite has already been used.'
                  : accept.error.code === 'expired'
                    ? 'This invite has expired. Ask your administrator to resend it.'
                    : accept.error.code === 'invalid_token'
                      ? 'This invite link is invalid.'
                      : accept.error.code === 'email_in_use'
                        ? 'A user with this email already exists in the clinic.'
                        : accept.error.message}
              </div>
            ) : null}
            <button type="submit" disabled={accept.isPending}>
              {accept.isPending ? 'Joining…' : 'Join the clinic'}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
